import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { AlertTriangle, Plus, Trash2, Radar } from "lucide-react";
import { RESTRICTION_LABEL, ENDORSEMENT_LABEL } from "@/components/drivers/Restrictions";

const CATEGORIES = ["Unsafe Driving", "Hours of Service", "Driver Fitness", "Controlled Substances", "Vehicle Maintenance", "Hazmat", "Crash", "Other"];
const SEV = { low: "bg-slate-100 text-slate-700 border-slate-200", medium: "bg-amber-50 text-amber-800 border-amber-300", high: "bg-rose-50 text-rose-700 border-rose-200" };

export function ViolationStatsBadge({ stats }) {
  if (!stats) return null;
  if (stats.flagged) return <Badge className="bg-rose-50 text-rose-700 border border-rose-200" data-testid="violation-flag-badge"><AlertTriangle className="h-3 w-3 mr-1" />Flagged · {stats.last_12mo} in 12 mo{stats.crashes_12mo ? ` · ${stats.crashes_12mo} crash` : ""}</Badge>;
  return <Badge variant="outline" data-testid="violation-count-badge">{stats.last_12mo} violation{stats.last_12mo === 1 ? "" : "s"} · 12 mo</Badge>;
}

export function ReviewBadge({ flags, isAdmin, testid, onOpen }) {
  if (!flags) return null;
  const ai = isAdmin ? flags.ai_pending : 0;
  const un = flags.unreviewed || 0;
  if (!ai && !un) return null;
  const click = onOpen ? (e) => { e.stopPropagation(); onOpen(); } : undefined;
  return (
    <span className={`inline-flex items-center gap-1 flex-wrap ${onOpen ? "cursor-pointer" : ""}`} data-testid={testid} onClick={click} role={onOpen ? "button" : undefined}>
      {ai > 0 && <Badge className="bg-violet-600 text-white border-violet-700 font-sans hover:bg-violet-700" title="AI read violations from this driver's MVR/PSP — click to approve or discard them"><Radar className="h-3 w-3 mr-1" />{ai} to review</Badge>}
      {un > 0 && <Badge className="bg-amber-100 text-amber-900 border border-amber-300 font-sans" title={isAdmin ? "Saved violations the customer hasn't reviewed yet" : "New violations — click to review"}><AlertTriangle className="h-3 w-3 mr-1" />{un} new{isAdmin ? " · customer" : ""}</Badge>}
    </span>
  );
}

export function useReviewFlags(deps) {
  const [map, setMap] = useState({});
  useEffect(() => {
    const fetch = () => api.get("/violations/review-flags").then(({ data }) => setMap(data)).catch(() => {});
    fetch();
    window.addEventListener("review-flags-changed", fetch);
    return () => window.removeEventListener("review-flags-changed", fetch);
  }, [deps]);
  return map;
}

export function ViolationsTab({ driverId, isAdmin }) {
  const [data, setData] = useState(null);
  const empty = { date: "", description: "", category: "Unsafe Driving", points: "", severity: "medium", is_crash: false };
  const [form, setForm] = useState(empty);
  const [adding, setAdding] = useState(false);
  const unreviewed = (data?.items || []).filter((v) => !v.customer_ack).length;

  const load = useCallback(async () => {
    try { const { data: d } = await api.get(`/drivers/${driverId}/violations`); setData(d); }
    catch (err) { toast.error(formatApiError(err)); }
  }, [driverId]);
  useEffect(() => { load(); }, [load]);
  const ack = async () => {
    try { await api.post(`/drivers/${driverId}/violations/ack`); toast.success("Violations marked as reviewed"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/drivers/${driverId}/violations`, { items: [{ ...form, points: form.points === "" ? null : Number(form.points), date: form.date || null, is_crash: form.is_crash || form.category === "Crash" }] });
      toast.success("Violation recorded · customer and admins notified"); setForm(empty); setAdding(false); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Remove this violation?")) return;
    try { await api.delete(`/violations/${id}`); load(); } catch (err) { toast.error(formatApiError(err)); }
  };

  if (!data) return <div className="text-sm text-slate-500 py-4">Loading…</div>;
  return (
    <div className="space-y-3" data-testid="violations-tab">
      {isAdmin && <AiDraftsPanel driverId={driverId} onSaved={load} />}
      {unreviewed > 0 && (
        <div className="flex items-center justify-between gap-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm" data-testid="violations-review-banner">
          <div className="flex items-center gap-2 text-amber-900"><AlertTriangle className="h-4 w-4" />{isAdmin ? `${unreviewed} violation(s) not yet reviewed by the customer` : `${unreviewed} new violation(s) added by your consortium — please review`}</div>
          {!isAdmin && <Button size="sm" className="h-7 bg-amber-600 hover:bg-amber-700 text-white" onClick={ack} data-testid="violations-ack-button">Mark reviewed</Button>}
        </div>
      )}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-sm">
          <ViolationStatsBadge stats={data.stats} />
          <span className="text-xs text-slate-500">{data.stats.points_12mo} pts · threshold {data.threshold} in 12 months or any crash</span>
        </div>
        <Button size="sm" variant="outline" onClick={() => setAdding((a) => !a)} data-testid="violation-add-toggle"><Plus className="h-4 w-4 mr-1" />Add violation</Button>
      </div>
      {adding && (
        <form onSubmit={add} className="grid grid-cols-2 md:grid-cols-6 gap-2 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm items-end" data-testid="violation-form">
          <div><Label className="text-xs">Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="violation-date-input" /></div>
          <div className="col-span-2"><Label className="text-xs">Description</Label><Input required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="e.g. 392.2S Speeding 6-10 over" data-testid="violation-description-input" /></div>
          <div><Label className="text-xs">Category</Label><Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v, is_crash: v === "Crash" })}><SelectTrigger data-testid="violation-category-select"><SelectValue /></SelectTrigger><SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
          <div><Label className="text-xs">Points</Label><Input type="number" step="0.5" min="0" value={form.points} onChange={(e) => setForm({ ...form, points: e.target.value })} data-testid="violation-points-input" /></div>
          <div className="flex items-center gap-2"><Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}><SelectTrigger className="w-24" data-testid="violation-severity-select"><SelectValue /></SelectTrigger><SelectContent>{["low", "medium", "high"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select><Button type="submit" size="sm" className="bg-blue-700 hover:bg-blue-800" data-testid="violation-save-button">Save</Button></div>
        </form>
      )}
      {data.items.length === 0 ? <div className="text-sm text-slate-500 py-3">No violations on record.</div> : (
        <div className="divide-y divide-slate-100 rounded-md border border-slate-200">
          {data.items.map((v) => (
            <div key={v.id} className="flex items-start justify-between gap-3 px-3 py-2 text-sm" data-testid={`violation-row-${v.id}`}>
              <div className="min-w-0">
                <div className="font-medium text-slate-900">{v.description}</div>
                <div className="text-xs text-slate-500 font-mono">{v.date || "no date"} · {v.category || "—"}{v.points != null ? ` · ${v.points} pts` : ""} · via {v.source}{v.created_by ? ` · ${v.created_by}` : ""}{!v.customer_ack && <span className="ml-1 text-amber-700 font-sans font-medium">· new</span>}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {v.is_crash && <Badge className="bg-rose-600 text-white border-rose-700">CRASH</Badge>}
                <Badge className={`${SEV[v.severity] || SEV.medium} border capitalize`}>{v.severity || "medium"}</Badge>
                <button type="button" onClick={() => remove(v.id)} className="text-slate-400 hover:text-rose-600" data-testid={`violation-delete-${v.id}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function PspMonitoringControl({ driver, onChanged }) {
  const mon = driver.psp_monitoring || {};
  const [cadence, setCadence] = useState(mon.cadence || "monthly");
  const set = async (enabled, cad = cadence) => {
    try {
      const { data } = await api.put(`/drivers/${driver.id}/psp-monitoring`, { enabled, cadence: cad });
      toast.success(enabled ? `PSP monitoring on · next re-order ${data.psp_monitoring.next_run}` : "PSP monitoring off"); onChanged(data);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <div className="col-span-2 flex flex-wrap items-center justify-between gap-3 rounded-md border border-slate-200 p-3" data-testid="psp-monitoring-control">
      <div className="flex items-center gap-2 text-sm"><Radar className="h-4 w-4 text-blue-700" /><div><div className="font-medium">PSP Monitoring</div><div className="text-xs text-slate-500">{mon.enabled ? `Auto re-order ${mon.cadence} · next ${mon.next_run}${mon.enrolled_by ? ` · enrolled by ${mon.enrolled_by}` : ""}` : "Automatically re-order this driver's PSP on a schedule"}</div></div></div>
      <div className="flex items-center gap-2">
        <Select value={cadence} onValueChange={(v) => { setCadence(v); if (mon.enabled) set(true, v); }}><SelectTrigger className="w-28 h-8" data-testid="psp-cadence-select"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="monthly">Monthly</SelectItem><SelectItem value="quarterly">Quarterly</SelectItem></SelectContent></Select>
        <Switch checked={!!mon.enabled} onCheckedChange={(v) => set(v)} data-testid="psp-monitoring-toggle" />
      </div>
    </div>
  );
}

export function LicenseSuggestion({ report, file, onReportChange }) {
  const [busy, setBusy] = useState(false);
  const lic = file.ai_license;
  if (!lic || lic.applied || !report.driver_id) return null;
  const codes = [...(lic.restrictions || []), ...(lic.intrastate_only ? ["K"] : [])];
  const act = async (dismiss) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/reports/${report.id}/files/${file.id}/apply-license`, null, { params: { dismiss } });
      toast.success(dismiss ? "Suggestion dismissed" : `License details saved to ${report.driver_name}`); onReportChange?.(data);
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-1 flex flex-wrap items-center gap-2 rounded-md border border-sky-200 bg-sky-50 px-2.5 py-1.5 text-xs" data-testid={`license-suggestion-${file.id}`}>
      <Radar className="h-3.5 w-3.5 text-sky-700" />
      <span className="text-sky-900">MVR shows{lic.class && <span className="ml-1 inline-flex items-center gap-1 rounded bg-white border border-blue-200 px-1.5 py-px"><b className="font-mono">Class {lic.class}</b>{lic.commercial === false && <span className="text-amber-700">non-commercial</span>}</span>}{lic.expires && <span className="ml-1 inline-flex items-center gap-1 rounded bg-white border border-blue-200 px-1.5 py-px"><span className="text-slate-600">valid to</span><b className="font-mono">{lic.expires}</b>{lic.status && lic.status !== "valid" && <span className="text-rose-700 uppercase">{lic.status}</span>}</span>}{(lic.endorsements || []).length > 0 && <span className="ml-1">endorsements {lic.endorsements.map((e) => <span key={e} className="ml-1 inline-flex items-center gap-1 rounded bg-white border border-blue-200 px-1.5 py-px"><b className="font-mono">{e}</b><span className="text-slate-600">{ENDORSEMENT_LABEL[e]}</span></span>)}</span>}{codes.length > 0 && <span className="ml-1">restrictions {codes.map((c) => <span key={c} className="ml-1 inline-flex items-center gap-1 rounded bg-white border border-sky-200 px-1.5 py-px"><b className="font-mono">{c}</b><span className="text-slate-600">{RESTRICTION_LABEL[c]}</span></span>)}</span>}{lic.restriction_text && <span className="ml-1 text-slate-500">("{lic.restriction_text}")</span>}</span>
      <span className="ml-auto inline-flex gap-1">
        <Button size="sm" className="h-6 px-2 bg-sky-700 hover:bg-sky-800 text-white" disabled={busy} onClick={() => act(false)} data-testid={`license-apply-${file.id}`}>Apply to driver</Button>
        <Button size="sm" variant="ghost" className="h-6 px-2" disabled={busy} onClick={() => act(true)} data-testid={`license-dismiss-${file.id}`}>Dismiss</Button>
      </span>
    </div>
  );
}

export function AiDraftsPanel({ driverId, onSaved }) {
  const [drafts, setDrafts] = useState([]);
  const [picked, setPicked] = useState({});
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get(`/drivers/${driverId}/violations/ai-drafts`).then(({ data }) => { setDrafts(data); setPicked(Object.fromEntries(data.flatMap((d) => d.items.map((_, i) => [`${d.file_id}:${i}`, true])))); }).catch(() => setDrafts([])), [driverId]);
  useEffect(() => { load(); }, [load]);
  if (!drafts.length) return null;
  const act = async (d, save) => {
    setBusy(true);
    try {
      const sel = d.items.filter((_, i) => picked[`${d.file_id}:${i}`]);
      if (save && sel.length) { await api.post(`/drivers/${driverId}/violations`, { items: sel }); toast.success(`${sel.length} violation(s) saved · customer notified`); }
      await api.post(d.cert_id ? `/certificates/${d.cert_id}/dismiss-suggestions` : `/reports/${d.report_id}/files/${d.file_id}/dismiss-suggestions`);
      if (!save) toast.success("AI drafts discarded");
      await load(); onSaved?.(); window.dispatchEvent(new Event("review-flags-changed"));
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-md border border-violet-300 bg-violet-50/60 p-3 space-y-2" data-testid="ai-drafts-panel">
      <div className="flex items-center gap-2 text-sm font-medium text-violet-900"><Radar className="h-4 w-4" />AI found {drafts.reduce((n, d) => n + d.items.length, 0)} violation(s) on this driver's report{drafts.length > 1 ? "s" : ""} — approve or discard</div>
      {drafts.map((d) => (
        <div key={d.file_id} className="rounded-md border border-violet-200 bg-white p-2 space-y-1" data-testid={`ai-draft-${d.file_id}`}>
          <div className="flex items-center justify-between gap-2 text-xs text-slate-600"><span className="truncate">{(d.report_types || []).join("/")} · {d.file_name}{d.received_at ? ` · pulled ${d.received_at.slice(0, 10)}` : ""}{d.cert_id ? " · from Certificates" : ""}</span><a className="text-blue-700 hover:underline shrink-0" href={d.cert_id ? `${process.env.REACT_APP_BACKEND_URL}/api/certificates/${d.cert_id}/file` : `${process.env.REACT_APP_BACKEND_URL}/api/reports/${d.report_id}/files/${d.file_id}`} target="_blank" rel="noreferrer" data-testid={`ai-draft-open-${d.file_id}`}>Open MVR</a></div>
          {d.items.map((it, i) => (
            <label key={`${d.file_id}-${i}`} className="flex items-start gap-2 text-xs">
              <input type="checkbox" checked={!!picked[`${d.file_id}:${i}`]} onChange={(e) => setPicked({ ...picked, [`${d.file_id}:${i}`]: e.target.checked })} className="mt-0.5" data-testid={`ai-draft-pick-${d.file_id}-${i}`} />
              <span><span className="font-mono text-slate-500">{it.date || "—"}</span> · {it.description} <span className="text-slate-500">({it.category}{it.points != null ? `, ${it.points} pts` : ""})</span>{it.is_crash && <Badge className="ml-1 bg-rose-600 text-white border-rose-700">CRASH</Badge>}</span>
            </label>
          ))}
          <div className="flex gap-2 pt-1">
            <Button size="sm" disabled={busy || !d.items.some((_, i) => picked[`${d.file_id}:${i}`])} onClick={() => act(d, true)} className="bg-violet-700 hover:bg-violet-800 h-7" data-testid={`ai-draft-save-${d.file_id}`}>Approve selected</Button>
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => act(d, false)} className="h-7" data-testid={`ai-draft-discard-${d.file_id}`}>Discard drafts</Button>
          </div>
        </div>
      ))}
    </div>
  );
}

export function ExtractViolationsButton({ report, file, onReportChange }) {
  const [items, setItems] = useState(null);
  const [picked, setPicked] = useState({});
  const [busy, setBusy] = useState(false);
  const pending = file.ai_status === "pending";
  const suggestions = file.ai_status === "done" ? (file.ai_suggestions || []) : null;

  useEffect(() => {
    if (!pending || !onReportChange) return;
    const iv = setInterval(async () => {
      try { const { data } = await api.get(`/reports/${report.id}`); const f = (data.files || []).find((x) => x.id === file.id); if (f && f.ai_status !== "pending") onReportChange(data); }
      catch (err) { console.warn("AI suggestion poll failed", err); }
    }, 4000);
    return () => clearInterval(iv);
  }, [pending, report.id, file.id, onReportChange]);

  useEffect(() => {
    if (suggestions && items === null) { setItems(suggestions); setPicked(Object.fromEntries(suggestions.map((_, i) => [i, true]))); }
  }, [suggestions, items]);

  if (!report.driver_id) return null;
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/reports/${report.id}/files/${file.id}/extract-violations`);
      setItems(data.items); setPicked(Object.fromEntries(data.items.map((_, i) => [i, true])));
      toast[data.items.length ? "success" : "info"](data.items.length ? `${data.items.length} record(s) found — review and save` : "No violations found in this document");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  const dismiss = async () => {
    setItems(null);
    if (file.ai_status === "done" && onReportChange) {
      try { const { data } = await api.post(`/reports/${report.id}/files/${file.id}/dismiss-suggestions`); onReportChange(data); } catch (err) { console.warn("dismiss suggestions failed", err); toast.error(formatApiError(err)); }
    }
  };
  const save = async () => {
    const sel = items.filter((_, i) => picked[i]);
    if (!sel.length) return;
    setBusy(true);
    try {
      await api.post(`/drivers/${report.driver_id}/violations`, { items: sel });
      toast.success(`${sel.length} violation(s) saved to ${report.driver_name} · notifications sent`); await dismiss();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-1">
      {pending && <div className="text-xs text-blue-700 inline-flex items-center gap-1" data-testid={`ai-pending-${file.id}`}><Radar className="h-3 w-3 animate-pulse" />AI is reading this report for violations…</div>}
      {!pending && items === null && <button type="button" onClick={run} disabled={busy} className="text-xs text-blue-700 hover:underline inline-flex items-center gap-1" data-testid={`extract-violations-${file.id}`}><Radar className="h-3 w-3" />{busy ? "Reading with AI…" : file.ai_status === "failed" ? "AI read failed — retry" : "Extract violations (AI)"}</button>}
      {items && (
        <div className="mt-1 rounded-md border border-slate-200 bg-slate-50 p-2 space-y-1" data-testid="extracted-violations">
          <div className="tag-mono text-slate-500">AI-EXTRACTED · review before saving</div>
          {items.length === 0 && <div className="text-xs text-slate-500">No violations found in this document.</div>}
          {items.map((it, i) => (
            <label key={`${it.date || "nd"}-${it.description}-${i}`} className="flex items-start gap-2 text-xs">
              <input type="checkbox" checked={!!picked[i]} onChange={(e) => setPicked({ ...picked, [i]: e.target.checked })} className="mt-0.5" data-testid={`extracted-pick-${i}`} />
              <span><span className="font-mono text-slate-500">{it.date || "—"}</span> · {it.description} <span className="text-slate-500">({it.category}{it.points != null ? `, ${it.points} pts` : ""})</span>{it.is_crash && <Badge className="ml-1 bg-rose-600 text-white border-rose-700">CRASH</Badge>}</span>
            </label>
          ))}
          <div className="flex gap-2 pt-1">
            <Button size="sm" onClick={save} disabled={busy || !items.length} className="bg-blue-700 hover:bg-blue-800 h-7" data-testid="extracted-save-button">Save selected</Button>
            <Button size="sm" variant="ghost" onClick={dismiss} className="h-7" data-testid="extracted-dismiss-button">{items.length ? "Discard" : "Dismiss"}</Button>
          </div>
        </div>
      )}
    </div>
  );
}
